import { NetworkStatus } from "@apollo/client";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useOverlayScrollbars } from "overlayscrollbars-react";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlusIcon } from "lucide-react";

import { TableColumnType } from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import {
  GridAppearance,
  useResolvedGridAppearance,
} from "@/components/organisms/Table/Table.tools";
import {
  TableStoreProvider,
  useTableStore,
  useTableStoreApi,
} from "@/components/organisms/Table/Table.store";
import { TableDialog } from "@/components/organisms/Table/TableDialog";
import { TableHeaderRow } from "@/components/organisms/Table/TableHeaderRow";
import { Row } from "@/components/organisms/Table/TableRow";
import { Values, ZIndex } from "@/constants";
import { useDevice } from "@/hooks/useDevice";
import { useResizeColumn } from "@/hooks/useResizeColumn";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import {
  sanitizeKeyForCSS,
  useContextCellNavigation,
  useContextScope,
} from "@/hooks/useContextTree";
import { State } from "@/state";
import { Button } from "@/components/ui/button";

const rowHeight = Values.DEFAULT_ROW_HEIGHT;
const defaultColumnWidth = 200;
const OVERSCAN_COUNT = 5; // Number of items to render before and after the visible area
// Placeholder rows past the full-render window on each side. A full row is
// expensive, a loading row is not, so fast scrolling that outruns the window
// shows skeletons instead of blank space until the window catches up.
const LOADING_OVERSCAN_COUNT = 10;

export { TableStoreProvider } from "@/components/organisms/Table/Table.store";

export function Table({
  columns = [],
  hideOuterBorders = false,
  hideCheckboxes = false,
  isLoading,
  loadNext,
  rows,
  totalCount,
  unlockedColumnsStartIndex: unlockedColumnsStartIndexOverride,
  onCheckedRowsButtonsProps,
  onCheckedRowsExtraActions,
  networkStatus,
  onAddNew,
  onRowClick: onRowClickProp,
  appearance,
}: {
  columns?: TableColumnType[];
  hideOuterBorders?: boolean;
  hideCheckboxes?: boolean;
  isLoading?: boolean;
  loadNext?: () => void;
  onAddNew?: () => void;
  rows: any[];
  totalCount: number;
  unlockedColumnsStartIndex?: number;
  onCheckedRowsButtonsProps?: ComponentProps<typeof TableDialog>["buttons"];
  onCheckedRowsExtraActions?: ComponentProps<
    typeof TableDialog
  >["extraActions"];
  networkStatus?: NetworkStatus;
  onRowClick?: (row: any) => void;
  appearance?: GridAppearance;
}) {
  // A phone - or a window as narrow - has nothing beside the list to show a
  // row in: tapped, the row opens in the panel, over the list.
  const { isNarrow } = useDevice();
  const onRowClick =
    onRowClickProp ??
    ((row: any) => {
      if (!row?.id) return;
      // Row clicks open the panel for inspection; the user is still navigating
      // the table, not editing a field. Don't autofocus the first field —
      // that's reserved for explicit "edit this object" entry points like the
      // ``FieldReferences`` adornment.
      if (isNarrow) {
        State.getState().openNodePanel(row.id, { autofocus: false });
      } else {
        State.getState().setActivePanelNodeId(row.id, { autofocus: false });
      }
    });
  return (
    <TableStoreProvider>
      <TableContent
        columns={columns}
        hideOuterBorders={hideOuterBorders}
        hideCheckboxes={hideCheckboxes}
        isLoading={isLoading}
        loadNext={loadNext}
        onAddNew={onAddNew}
        rows={rows}
        totalCount={totalCount}
        // Columns an object marks ``isLockedLeft`` at the start of its list
        // (a ledger row's entity box and title) stay put while the rest
        // scroll; with none marked, the first column does.
        initialUnlockedColumnsLayoutIndex={
          unlockedColumnsStartIndexOverride ??
          Math.max(
            1,
            columns.findIndex((column) => !column.isLockedLeft) === -1
              ? columns.length
              : columns.findIndex((column) => !column.isLockedLeft),
          )
        }
        onCheckedRowsButtonsProps={onCheckedRowsButtonsProps}
        onCheckedRowsExtraActions={onCheckedRowsExtraActions}
        networkStatus={networkStatus}
        onRowClick={onRowClick}
        appearance={appearance}
      />
    </TableStoreProvider>
  );
}

function TableContent({
  columns = [],
  hideOuterBorders = false,
  hideCheckboxes = false,
  isLoading,
  loadNext,
  rows,
  totalCount,
  initialUnlockedColumnsLayoutIndex = 1,
  onCheckedRowsButtonsProps,
  onCheckedRowsExtraActions,
  networkStatus,
  onAddNew,
  onRowClick = (row) => {
    if (!row?.id) return;
    State.getState().setActivePanelNodeId(row.id, { autofocus: false });
  },
  appearance,
}: {
  columns?: TableColumnType[];
  hideOuterBorders?: boolean;
  hideCheckboxes?: boolean;
  isLoading?: boolean;
  loadNext?: () => void;
  onAddNew?: () => void;
  rows: any[];
  totalCount: number;
  initialUnlockedColumnsLayoutIndex?: number;
  onCheckedRowsButtonsProps?: ComponentProps<typeof TableDialog>["buttons"];
  onCheckedRowsExtraActions?: ComponentProps<
    typeof TableDialog
  >["extraActions"];
  networkStatus?: NetworkStatus;
  onRowClick?: (row: any) => void;
  appearance?: GridAppearance;
}) {
  const storeApi = useTableStoreApi();
  const gridColors = useResolvedGridAppearance(appearance);
  const rootRef = useRef<HTMLDivElement>(null);
  const subRootRef = useRef<HTMLDivElement>(null);
  const subRootScrollRef = useRef<HTMLDivElement>(null);

  totalCount = isLoading && totalCount == 0 ? 8 : totalCount;

  // Seed from the viewport, not a flat 100. The ResizeObserver below measures
  // the real container, but only after mount — so the first render used to
  // ask for 110 rows (100 + overscan). With a page size of 100 that trips the
  // ``loadNext`` guard below (``endRow >= rows.length - loadBuffer``) on the
  // very first paint, so a list that had just been served whole from the
  // cache immediately fetched its next page. An estimate from the window is
  // never far off and is always well under a full page.
  const [visibleRowCount, setVisibleRowCount] = useState(() =>
    Math.max(1, Math.ceil(window.innerHeight / rowHeight)),
  );
  const visibleRowCountRef = useRef(visibleRowCount);
  visibleRowCountRef.current = visibleRowCount;

  // State to trigger loadNext in useEffect
  const [shouldTriggerLoadNext, setShouldTriggerLoadNext] = useState(false);

  // Use motion values instead of useState
  const scrollLeft = useMotionValue(0);

  // Negative transform values for translation
  const negativeScrollLeft = useTransform(scrollLeft, (value) => -value);
  // The locked columns' edge casts a shadow over what is scrolled under them -
  // only while something is, as a field's table does.
  const lockedShadowOpacity = useTransform(scrollLeft, (value) =>
    value > 0 ? 1 : 0,
  );

  // Get column definitions and layout order from the store individually
  const columnDefinitions = useTableStore((state) => state.columnDefinitions);
  const layoutIndexToColumnIndex = useTableStore(
    (state) => state.layoutIndexToColumnIndex,
  );
  const unlockedColumnsStartIndex = useTableStore(
    (state) => state.unlockedColumnsStartIndex,
  );

  // Reorder column definitions based on layoutIndexToColumnIndex from the store
  const orderedColumnDefinitions = useMemo(() => {
    // Map stored column indices back to the actual column definitions
    const defsByOriginalIndex: {
      [key: number]: (typeof columnDefinitions)[0];
    } = {};
    columnDefinitions.forEach((def) => {
      defsByOriginalIndex[def.originalIndex] = def;
    });
    return layoutIndexToColumnIndex
      .map((originalIndex) => defsByOriginalIndex[originalIndex])
      .filter(Boolean);
  }, [layoutIndexToColumnIndex, columnDefinitions]);

  // Filter ordered columns into locked and unlocked based on unlockedColumnsStartIndex
  const orderedLockedColumns = useMemo(() => {
    const lockedDefs = orderedColumnDefinitions.slice(
      0,
      unlockedColumnsStartIndex,
    );
    // Map definitions back to the original ColumnType array from props
    return lockedDefs
      .map((def) => columns.find((c) => c.objectKey === def.objectKey))
      .filter((c): c is TableColumnType => !!c);
  }, [orderedColumnDefinitions, unlockedColumnsStartIndex, columns]);

  const orderedUnlockedColumns = useMemo(() => {
    const unlockedDefs = orderedColumnDefinitions.slice(
      unlockedColumnsStartIndex,
    );
    // Map definitions back to the original ColumnType array from props
    return unlockedDefs
      .map((def) => columns.find((c) => c.objectKey === def.objectKey))
      .filter((c): c is TableColumnType => !!c);
  }, [orderedColumnDefinitions, unlockedColumnsStartIndex, columns]);

  // Calculate the total width of locked columns for horizontal scroll offset
  const getLockedColumnsWidth = useCallback(() => {
    let totalWidth = hideCheckboxes ? 0 : Values.DEFAULT_ROW_HEIGHT;
    for (let i = 0; i < unlockedColumnsStartIndex; i++) {
      const def = orderedColumnDefinitions[i];
      if (def) {
        totalWidth += def.width || Values.DEFAULT_COLUMN_WIDTH;
      }
    }
    return totalWidth;
  }, [orderedColumnDefinitions, unlockedColumnsStartIndex]);

  const { id: contextId, Scope, ref, node } = useContextScope();
  const { getClassName, getCellProps, getCell } = useContextCellNavigation(
    contextId,
    ref,
    node,
    {
      scrollBuffer: 1,
      wrap: false,
      scrollOffsetLeft: getLockedColumnsWidth,
      tab: true,
    },
  );

  // Set up resize handler - PASS ORDERED COLUMNS (using ordered definitions).
  // Do not pass containerWidth: live width would re-render the table on every
  // panel resize tick. Percentage columns are handled by FieldTable's mode.
  const { handleMouseDown } = useResizeColumn(
    orderedColumnDefinitions, // Pass the reordered definitions (they have originalIndex)
    {
      minWidth: Values.DEFAULT_ROW_HEIGHT,
      contextId,
      initialOffset: hideCheckboxes ? 0 : Values.DEFAULT_ROW_HEIGHT,
    },
  );
  const totalHeight = totalCount * rowHeight;

  // Get current scroll values for calculations
  const [startEndRow, setStartEndRow] = useState(() => ({
    startRow: 0,
    endRow: Math.min(totalCount, visibleRowCount + OVERSCAN_COUNT * 2), // Initial render range
  }));

  // Ref to store the previous scroll top value
  const prevScrollTopRef = useRef<number>(0);
  const preservedScrollLeftRef = useRef(0);
  const preservedScrollTopRef = useRef(0);

  // Optimized scroll handler
  const handleScroll = useCallback(() => {
    if (subRootScrollRef.current) {
      const scrollTop = subRootScrollRef.current.scrollTop;
      let scrollLeftVal = subRootScrollRef.current.scrollLeft;
      const preservedLeft = preservedScrollLeftRef.current;

      // Layout churn (e.g. cell defocus or brief empty rows) can snap scrollLeft to 0.
      if (
        preservedLeft > 50 &&
        scrollLeftVal < 10 &&
        preservedLeft - scrollLeftVal > 50 &&
        rows.length > 0
      ) {
        subRootScrollRef.current.scrollLeft = preservedLeft;
        scrollLeftVal = preservedLeft;
        scrollLeft.set(preservedLeft);
      } else {
        preservedScrollLeftRef.current = scrollLeftVal;
        preservedScrollTopRef.current = scrollTop;
      }

      // Update scrollLeft motion value regardless of scroll direction
      scrollLeft.set(scrollLeftVal);

      // Only proceed with vertical scroll logic if scrollTop has changed
      if (Math.abs(scrollTop - prevScrollTopRef.current) > 10) {
        prevScrollTopRef.current = scrollTop; // Update the ref

        const firstVisibleRowIndex = Math.floor(scrollTop / rowHeight);

        // Calculate new potential start and end rows based on overscanning
        const potentialStartRow = Math.max(
          0,
          firstVisibleRowIndex - OVERSCAN_COUNT,
        );
        const potentialEndRow = Math.min(
          totalCount, // Use totalCount as the upper bound
          firstVisibleRowIndex + visibleRowCount + OVERSCAN_COUNT,
        );

        // Check if we need to update the rendered range
        setStartEndRow((prev) => {
          // Estimate the first visible row index based on the previous state
          const prevFirstVisible = Math.max(0, prev.startRow + OVERSCAN_COUNT);
          const distance = Math.abs(firstVisibleRowIndex - prevFirstVisible);
          // Update if scrolled more than half the overscan buffer, or if the range needs to expand
          const threshold = OVERSCAN_COUNT / 2;

          const shouldUpdate =
            distance > threshold ||
            potentialEndRow > prev.endRow ||
            potentialStartRow < prev.startRow;

          const nextState = shouldUpdate
            ? {
                startRow: potentialStartRow,
                endRow: potentialEndRow,
              }
            : prev;

          // Condition to load next: Trigger if the bottom of the rendered range is near the end of loaded data
          const loadBuffer = 5; // Load when within 5 rows of the end of loaded data
          if (
            loadNext &&
            !isLoading &&
            networkStatus !== NetworkStatus.fetchMore &&
            rows.length < totalCount &&
            nextState.endRow >= rows.length - loadBuffer
          ) {
            // Set state to trigger loadNext in useEffect instead of calling directly
            setShouldTriggerLoadNext(true);
          }

          return nextState; // Return the potentially updated state
        });
      }
    }
  }, [
    totalCount,
    rows.length,
    loadNext,
    isLoading,
    visibleRowCount,
    scrollLeft,
    networkStatus,
  ]);

  // Restore horizontal scroll after row data briefly clears and repopulates.
  useLayoutEffect(() => {
    const el = subRootScrollRef.current;
    if (!el || rows.length === 0) return;

    const savedLeft = preservedScrollLeftRef.current;
    const savedTop = preservedScrollTopRef.current;
    if (savedLeft > 0 && el.scrollLeft < savedLeft - 10) {
      el.scrollLeft = savedLeft;
      scrollLeft.set(savedLeft);
    }
    if (savedTop > 0 && el.scrollTop < savedTop - 10) {
      el.scrollTop = savedTop;
    }
  }, [rows.length, scrollLeft]);

  // useEffect to call loadNext when triggered
  useEffect(() => {
    if (shouldTriggerLoadNext && loadNext) {
      loadNext();
      setShouldTriggerLoadNext(false); // Reset the trigger
    }
  }, [shouldTriggerLoadNext, loadNext]);

  // Create a mapping of objectKey to index once
  const objectKeyToIndex = useMemo(() => {
    const map: Record<string, number> = {}; // Add type annotation
    columnDefinitions.forEach((col) => {
      // Use originalIndex from columnDefinitions (set in useEffect)
      map[col.objectKey] = col.originalIndex;
    });
    return map;
  }, [columnDefinitions]);

  const getRowIdFromRowData = useTableStore(
    (state) => state.getRowIdFromRowData,
  );

  useEffect(() => {
    const newCheckedRows = {
      ...storeApi.getState().checkedRows,
    };
    rows.forEach((row) => {
      const rowId = getRowIdFromRowData(row);
      if (!(rowId in newCheckedRows)) {
        newCheckedRows[rowId] = false;
      }
    });
    storeApi.setState({
      checkedRows: newCheckedRows,
    });
  }, [getRowIdFromRowData, rows, storeApi]);

  useEffect(() => {
    storeApi.setState({
      checkedRows: {},
      columnDefinitions: columns.map((col, index) => ({
        width: col.width || defaultColumnWidth,
        widthMode: col.widthMode,
        originalIndex: index,
        objectKey: col.objectKey,
        label: col.label,
      })),
      isAllRowsChecked: false,
      onRowClick,
      getRowIdFromRowData: (row) => row?.id ?? row?.identifier ?? "",
      layoutIndexToColumnIndex: Array.from(
        { length: columns.length },
        (_, index) => index,
      ),
      unlockedColumnsStartIndex: initialUnlockedColumnsLayoutIndex,
    });
  }, [columns, initialUnlockedColumnsLayoutIndex, onRowClick, storeApi]);

  // Reset the virtualized window only for a fresh load (filter/tab change), not
  // when fetchMore appends rows or totalCount arrives while data is on screen.
  useEffect(() => {
    if (rows.length > 0) {
      return;
    }
    setStartEndRow({
      startRow: 0,
      endRow: Math.min(totalCount, visibleRowCount + OVERSCAN_COUNT * 2),
    });
  }, [rows.length, totalCount, visibleRowCount]);

  // Keep the virtualized row window aligned with scroll position when the
  // viewport height is remeasured or additional pages are appended.
  useEffect(() => {
    if (rows.length === 0) {
      return;
    }
    const scrollTop = subRootScrollRef.current?.scrollTop ?? 0;
    const firstVisibleRowIndex = Math.floor(scrollTop / rowHeight);
    setStartEndRow({
      startRow: Math.max(0, firstVisibleRowIndex - OVERSCAN_COUNT),
      endRow: Math.min(
        totalCount,
        firstVisibleRowIndex + visibleRowCount + OVERSCAN_COUNT,
      ),
    });
  }, [visibleRowCount, rows.length, totalCount]);

  // Back from a routed detail page opened from one of this table's cells
  // (``detailOriginCell``): render that row, then focus the cell below. The
  // first table to load consumes the origin whether or not it holds the row,
  // so one left behind by leaving for another view never outlives that list.
  // Declared after the window effect above so this window wins on arrival.
  const [cellToFocus, setCellToFocus] = useState<{
    rowIndex: number;
    colIndex: number;
  } | null>(null);
  useEffect(() => {
    if (isLoading || rows.length === 0) {
      return;
    }
    const origin = State.getState().detailOriginCell;
    if (!origin) {
      return;
    }
    State.setState({ detailOriginCell: null });
    const rowIndex = rows.findIndex(
      (row) => getRowIdFromRowData(row) === origin.rowId,
    );
    if (rowIndex < 0) {
      return;
    }
    setStartEndRow({
      startRow: Math.max(0, rowIndex - OVERSCAN_COUNT),
      endRow: Math.min(totalCount, rowIndex + visibleRowCount + OVERSCAN_COUNT),
    });
    setCellToFocus({ rowIndex, colIndex: origin.colIndex });
    // Keyed on arrival of the rows alone: a once-per-load decision.
  }, [isLoading, rows]);

  // ``focus`` scrolls the cell into view, and the scroll handler re-centres
  // the rendered window on it.
  useEffect(() => {
    if (!cellToFocus) {
      return;
    }
    getCell(cellToFocus.rowIndex, cellToFocus.colIndex)?.focus();
    setCellToFocus(null);
  }, [cellToFocus, getCell]);

  const [initialize] = useOverlayScrollbars({
    defer: true,
    options: {
      scrollbars: {},
    },
  });

  useEffect(() => {
    const { current: subRootElement } = subRootRef;
    const { current: rootElement } = rootRef;

    if (subRootElement && rootElement) {
      initialize({
        target: rootElement,
        elements: {
          viewport: subRootElement,
        },
      });
    }
  }, [initialize]);

  // Extract startRow and endRow for use in rendering
  const { startRow, endRow } = startEndRow;
  const renderStartRow = Math.max(0, startRow - LOADING_OVERSCAN_COUNT);
  const renderEndRow = Math.min(totalCount, endRow + LOADING_OVERSCAN_COUNT);

  const isInitialLoad = Boolean(isLoading && rows.length === 0);

  const resizeRef = useResizeObserver((entry) => {
    const nextVisibleRowCount = Math.max(
      1,
      Math.floor(entry.height / rowHeight),
    );
    if (nextVisibleRowCount !== visibleRowCountRef.current) {
      setVisibleRowCount(nextVisibleRowCount);
    }
  });

  return (
    <Scope
      ref={resizeRef}
      data-testid={`table-container-rows-${rows?.length ?? 0}`}
      style={{
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        height: "100%",
        minHeight: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        position: "relative",
        paddingTop: Values.DEFAULT_HEADER_HEIGHT,
      }}
    >
      <div
        onWheel={(e) => {
          if (subRootScrollRef.current) {
            // Forward horizontal scroll (trackpad gesture or shift+wheel) to the scrollable container
            subRootScrollRef.current.scrollLeft += e.deltaX || e.deltaY;
          }
        }}
      >
        <TableHeaderRow
          getClassName={getClassName}
          handleMouseDown={handleMouseDown}
          hideOuterBorders={hideOuterBorders}
          hideCheckboxes={hideCheckboxes}
          lockedColumns={orderedLockedColumns}
          negativeScrollLeft={negativeScrollLeft}
          objectKeyToIndex={objectKeyToIndex}
          rowHeight={Values.DEFAULT_HEADER_HEIGHT}
          unlockedColumns={orderedUnlockedColumns}
          gridColors={gridColors}
          totalCount={totalCount}
          isLoading={isLoading}
        />
      </div>
      {(() => {
        // Where the locked columns end: the last one's offset and width, as
        // the columns are drawn and resized - or the row numbers' column alone.
        const last = orderedLockedColumns[orderedLockedColumns.length - 1];
        const key = last ? sanitizeKeyForCSS(last.objectKey) : null;
        const edge = key
          ? `calc(var(--${contextId}-column-${key}-offset) + var(--${contextId}-column-${key}-width))`
          : hideCheckboxes
            ? null
            : `${Values.DEFAULT_ROW_HEIGHT}px`;
        return edge ? (
          <motion.div
            aria-hidden
            data-testid="table-locked-shadow"
            className="absolute top-0 bottom-0 w-2 pointer-events-none [background:linear-gradient(to_right,rgb(0_0_0_/_0.05),transparent)] [transition:opacity_0.2s_ease]"
            style={{
              left: edge,
              opacity: lockedShadowOpacity,
              zIndex: ZIndex.GRID.HEADER_LOCKED,
            }}
          />
        ) : null;
      })()}
      <div
        ref={rootRef}
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
        }}
      >
        <div
          ref={(el) => {
            if (el) {
              subRootRef.current = el;
              subRootScrollRef.current = el;
            }
          }}
          onScroll={handleScroll}
          style={{
            position: "relative",
            height: "100%",
            width: "100%",
            overflowX: "auto",
            overflowY: "auto",
          }}
        >
          {/* Width spacer ensures horizontal scroll works even with no rows */}
          <div
            style={{
              width: `var(--${contextId}-total-width)`,
              height: 1,
              marginTop: -1,
              pointerEvents: "none",
            }}
          />
          {/* Rows Container */}
          <motion.div
            style={{
              position: "sticky",
              left: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              height: totalHeight || "100%",
            }}
          >
            {/* Virtualized Rows */}
            <div
              style={{
                height: renderStartRow * rowHeight,
                width: "100%",
              }}
            />
            {/* Top Spacer */}
            {Array.from({
              length: Math.max(0, renderEndRow - renderStartRow),
            }).map((_, i) => {
              const rowIndex = renderStartRow + i;

              // Boundary check just in case totalCount changes rapidly
              if (rowIndex >= totalCount) return null;

              const row = rows?.[rowIndex];
              const isRowLoading =
                isInitialLoad ||
                rowIndex >= rows.length ||
                rowIndex < startRow ||
                rowIndex >= endRow;

              return (
                <Row
                  // Use a stable key including the actual row ID if available, otherwise index
                  key={`row-${rowIndex}`}
                  hideOuterBorders={hideOuterBorders}
                  hideCheckboxes={hideCheckboxes}
                  isLastRow={rowIndex === totalCount - 1}
                  isLoading={isRowLoading}
                  lockedColumns={orderedLockedColumns}
                  negativeScrollLeft={negativeScrollLeft}
                  onClick={onRowClick}
                  orderedColumnDefinitions={orderedColumnDefinitions}
                  row={row}
                  rowIndex={rowIndex}
                  contextId={contextId}
                  unlockedColumns={orderedUnlockedColumns}
                  getCellProps={getCellProps}
                  gridColors={gridColors}
                />
              );
            })}
            <div
              style={{
                height:
                  Math.max(0, (totalCount || 0) - (renderEndRow || 0)) *
                  rowHeight,
                width: "100%",
              }}
            />{" "}
            {/* Bottom Spacer */}
          </motion.div>
        </div>
      </div>
      {isLoading && rows.length === 0 && (
        <div data-testid="view-loading" style={{ display: "none" }} />
      )}
      <TableDialog
        buttons={onCheckedRowsButtonsProps}
        extraActions={onCheckedRowsExtraActions}
        nameAsSingular="row"
        totalCount={isLoading ? undefined : totalCount}
      />

      {onAddNew && (
        <div className="absolute bottom-4 left-4 z-10">
          <Button
            variant="secondary"
            size="sm"
            onClick={onAddNew}
            className="shadow-md pl-4 pr-4"
          >
            <Icon ReactIcon={PlusIcon} size="sm" />
            Add
          </Button>
        </div>
      )}
    </Scope>
  );
}
